import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const ToastContext = React.createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'info', duration = 3000) => {
    const id = Date.now();
    setToasts(prevToasts => [...prevToasts, { id, message, type }]);
    setTimeout(() => removeToast(id), duration);
  };

  const removeToast = (id) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} />
    </ToastContext.Provider>
  );
};

export const useToast = () => React.useContext(ToastContext);

const ToastContainer = ({ toasts }) => (
  <div className="fixed bottom-4 right-4 z-50">
    <AnimatePresence>
      {toasts.map(toast => (
        <Toast key={toast.id} {...toast} />
      ))}
    </AnimatePresence>
  </div>
);

const Toast = ({ id, message, type }) => {
  const { removeToast } = useToast();

  const variants = {
    initial: { opacity: 0, y: 50, scale: 0.3 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, scale: 0.5, transition: { duration: 0.2 } }
  };

  const getToastStyle = () => {
    switch (type) {
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-yellow-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <motion.div
      layout
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      className={`${getToastStyle()} text-white p-4 rounded-lg shadow-lg flex items-center justify-between mb-4`}
    >
      <span>{message}</span>
      <button onClick={() => removeToast(id)} className="ml-4 focus:outline-none">
        <X size={18} />
      </button>
    </motion.div>
  );
};